import React, { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { theme, useResponsive } from '@sg/garnish';

import { GiftCard, useRedeemGiftCardDefaultValues } from '@order/features-gift-card';
import { useTelemetry } from '@order/Telemetry';

import { AccountScreenContainer, AccountScreenSection, AccountScreenTitle } from '../../components';
import { useGiftCardBalance, useGiftCardContent, useGiftCardOrders } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountGiftCardsScreen = () => {
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const router = useRouter();
  const { track } = useTelemetry();

  // ─── Queries ───────────────────────────────────────────────────────────────

  const giftCardBalanceData = useGiftCardBalance();
  const giftCardOrdersData = useGiftCardOrders();
  const contentfulData = useGiftCardContent();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleOnRequestGiftCardPurchase = useCallback(() => {
    track('account.gift.header.pressed');

    router.dismissAll();
    router.navigate('/gift');
  }, [track, router]);

  const handleOnRequestGiftCardRedemption = useCallback(() => {
    track('account.gift.redemption.pressed');

    router.navigate('/add-gift-card');
  }, [track, router]);

  const getConfirmationLink = useCallback((id: string) => {
    return `/(tabs)/gift/confirmation/${id}?referrer=gift-card-orders`;
  }, []);

  const handleOrderPress = useCallback(
    (orderId: string) => {
      router.navigate(`/(tabs)/gift/confirmation/${orderId}?referrer=gift-card-orders`);
    },
    [router],
  );

  // ─── Gift Cards ────────────────────────────────────────────────────────────

  const routeParams = useLocalSearchParams<{ giftCardId?: string }>();
  const giftCardRedemptionEncodedData = routeParams.giftCardId;

  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  // ─── Rendering ─────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="gift-cards">
      {currentBreakpoint.isXS ? <AccountScreenTitle title={formatMessage(messages.title)} /> : null}

      <AccountScreenSection style={styles.content}>
        <GiftCard.Account.Header {...contentfulData} onPress={handleOnRequestGiftCardPurchase} />

        <GiftCard.Account.Info
          {...giftCardBalanceData}
          {...defaultRedemptionValues}
          onRedeemGiftCardPress={handleOnRequestGiftCardRedemption}
        />

        <GiftCard.Account.Orders
          {...giftCardOrdersData}
          onOpenGiftCardsScreen={handleOnRequestGiftCardPurchase}
          getConfirmationLink={getConfirmationLink}
          onOrderPress={handleOrderPress}
        />
      </AccountScreenSection>
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    gap: theme.spacing['10'],
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Gift Cards',
    description: 'Gift Cards Screen | Title',
  },
});
